<script lang="ts">
	import { createEventDispatcher } from 'svelte';
	import { format } from 'svelte-i18n';
	import { Stack, Select } from '@Shelly/shelly-design-system';
	import type { FlowNode } from '../../types/flow-types';
	import BaseInspector from '../BaseInspector.svelte';
	import { TimeConditionSubType } from '../../converters/TypeRegistry';

	// Reuse the actual Scenes time-based sub-components
	import DailyTriggerV2 from '../../../Scenes/SceneWizard/Process/When/AddEditContionWizard/Steps/TimeBased/Components/DailyTriggerV2.svelte';
	import OneTimeTriggerV2 from '../../../Scenes/SceneWizard/Process/When/AddEditContionWizard/Steps/TimeBased/Components/OneTimeTriggerV2.svelte';
	import TimerTriggerV2 from '../../../Scenes/SceneWizard/Process/When/AddEditContionWizard/Steps/TimeBased/Components/TimerTriggerV2.svelte';

	import type { DailyWeeklyCondition, ExecuteOnceAt } from '../../../../types/TimeBasedConditionsUI';

	export let node: FlowNode;

	const dispatch = createEventDispatcher<{
		update: FlowNode;
	}>();

	// Local state
	let timeSubType: string = (node.data.timeSubType as string) || TimeConditionSubType.Daily;

	// State objects for the Scene sub-components
	let dailyValue: DailyWeeklyCondition = {
		isin: (node.data.weekdays as number[]) || [1, 2, 3, 4, 5],
		time: (node.data.time as string) || '08:00',
	};

	let onceValue: ExecuteOnceAt = {
		date: (node.data.date as string) || new Date().toISOString().split('T')[0],
		time: (node.data.executeTime as string) || '12:00',
	};

	let timerInterval: string | number = Number(node.data.interval) || 30;

	// Guard: only re-initialize when a DIFFERENT node is selected.
	// Without this, updateNodeSilent() sets node = {...} (same id, new ref),
	// which triggers $: if (node) again → syncXToNode → infinite loop.
	let lastInitializedNodeId = '';
	$: isDedicatedExecuteOnce = node.data.paletteKey === 'executeOnce';
	$: if (node && node.id !== lastInitializedNodeId) {
		lastInitializedNodeId = node.id;
		timeSubType = isDedicatedExecuteOnce
			? TimeConditionSubType.ExecuteOnce
			: (node.data.timeSubType as string) || TimeConditionSubType.Daily;
		dailyValue = {
			isin: (node.data.weekdays as number[]) || [1, 2, 3, 4, 5],
			time: (node.data.time as string) || '08:00',
		};
		onceValue = {
			date: (node.data.date as string) || new Date().toISOString().split('T')[0],
			time: (node.data.executeTime as string) || '12:00',
		};
		timerInterval = Number(node.data.interval) || 30;
	}

	// Time type options
	const typeOptions = [
		{
			value: TimeConditionSubType.Timer,
			title: $format('time_type_timer', { default: 'Timer (Interval)' }),
		},
		{
			value: TimeConditionSubType.Daily,
			title: $format('time_type_daily', { default: 'Daily/Weekly' }),
		},
		{
			value: TimeConditionSubType.ExecuteOnce,
			title: $format('time_type_once', { default: 'Execute Once' }),
		},
	];

	function handleTypeChange(event: CustomEvent) {
		const detail = event.detail;
		timeSubType = typeof detail === 'string' ? detail : detail?.value || detail;
		updateNode();
	}

	// Sync sub-component state back to node — called reactively
	$: {
		// Sync dailyValue changes
		if (timeSubType === TimeConditionSubType.Daily && dailyValue) {
			syncDailyToNode();
		}
	}

	$: {
		// Sync onceValue changes
		if (timeSubType === TimeConditionSubType.ExecuteOnce && onceValue) {
			syncOnceToNode();
		}
	}

	$: {
		// Sync timerInterval changes
		if (timeSubType === TimeConditionSubType.Timer && timerInterval !== undefined) {
			syncTimerToNode();
		}
	}

	function syncDailyToNode() {
		node.data.weekdays = dailyValue.isin;
		node.data.time = dailyValue.time;
		node.data.timeSubType = timeSubType;
		updateNodeSilent();
	}

	function syncOnceToNode() {
		node.data.date = onceValue.date;
		node.data.executeTime = onceValue.time;
		node.data.timeSubType = timeSubType;
		updateNodeSilent();
	}

	function syncTimerToNode() {
		node.data.interval = Number(timerInterval);
		node.data.timeSubType = timeSubType;
		updateNodeSilent();
	}

	function updateNode() {
		node.data.timeSubType = timeSubType;
		node.data.displayLabel = generateLabel();
		node = { ...node, data: { ...node.data } };
		dispatch('update', node);
	}

	function updateNodeSilent() {
		node.data.displayLabel = generateLabel();
		node = { ...node, data: { ...node.data } };
		dispatch('update', node);
	}

	function generateLabel(): string {
		const weekdayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
		if (timeSubType === TimeConditionSubType.Timer) {
			return `Every ${timerInterval} minutes`;
		} else if (timeSubType === TimeConditionSubType.Daily) {
			const dayLabels =
				dailyValue.isin.length === 7
					? 'every day'
					: dailyValue.isin.map((d) => weekdayLabels[d] || d).join(', ');
			return `Daily at ${dailyValue.time} (${dayLabels})`;
		} else {
			return `Once on ${onceValue.date} at ${onceValue.time}`;
		}
	}
</script>

<div class="time-condition-inspector">
	<BaseInspector
		{node}
		title={isDedicatedExecuteOnce
			? $format('flow_execute_once_settings', { default: 'Execute Once Settings' })
			: $format('flow_time_condition_settings', { default: 'Time Condition Settings' })}
		on:update
	>
		<Stack direction="row" gap="s">
			<!-- Time Type Selection -->
			{#if !isDedicatedExecuteOnce}
				<Select
					icon=""
					aria-label="Time condition type"
					id="time-type"
					label={$format('label_time_type', { default: 'Time Type' })}
					options={typeOptions}
					value={timeSubType}
					on:change={handleTypeChange}
				/>
			{/if}

			<!-- Render the actual Scenes sub-components based on type -->
			<div class="time-component-wrapper">
				{#if timeSubType === TimeConditionSubType.Timer}
					<TimerTriggerV2 bind:input_val={timerInterval} />
				{:else if timeSubType === TimeConditionSubType.Daily}
					<DailyTriggerV2 bind:value={dailyValue} />
				{:else if timeSubType === TimeConditionSubType.ExecuteOnce}
					<OneTimeTriggerV2 bind:value={onceValue} />
				{/if}
			</div>
		</Stack>
	</BaseInspector>
</div>

<style>
	.time-condition-inspector {
		display: flex;
		flex-direction: column;
		gap: 16px;
	}
	.time-component-wrapper {
		padding: 8px 0;
	}

	.time-component-wrapper :global(.mb-s) {
		margin-bottom: 0.5rem;
	}

	.time-component-wrapper :global(.mb-m) {
		margin-bottom: 1rem;
	}

	.time-component-wrapper :global(.mb-l) {
		margin-bottom: 1.5rem;
	}

	.time-component-wrapper :global(.text-center) {
		text-align: center;
	}
</style>
